import { graphql } from '~/graphql';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';

export const useGetPackingList = (orderId: string) => {
  const query = graphql(`
    query GetPackingList($orderId: ID!) {
      packingList(orderId: $orderId) {
        id
        notes
        createdAt
        updatedAt
        items {
          id
          piecesPerBundleOverride
          price {
            amount
            currency
          }
          platformProductsLengths {
            id
            isNominal
            lengthDisplayValue
            length {
              nominalLengthInches
              actualLengthInches
            }
            platformProduct {
              name
              standardUnitsCode
              nominalWidthInches
              actualWidthInches
              nominalThicknessInches
              actualThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
                unit
              }
            }
          }
          quantity(mode: { mode: STANDARD }) {
            value
            unit
          }
          piecesQuantity: quantity(mode: { mode: STORED }) {
            value
          }
        }
      }
    }
  `);

  return useGraphqlQuery(query, ['packingList', orderId], { orderId });
};

export const useGetSellerProducts = (options?: { enabled?: boolean }) => {
  const query = graphql(`
    query GetSellerProducts($pageInput: PageInput!) {
      sellerProductInventories(pageInput: $pageInput) {
        content {
          id
          sellerProductConfiguration {
            price {
              pricePerUnit {
                amount
                currency
              }
            }
            platformProductLength {
              id
              isNominal
              lengthDisplayValue
              length {
                nominalLengthInches
                actualLengthInches
              }
              platformProduct {
                name
                standardUnitsCode
                nominalWidthInches
                actualWidthInches
                nominalThicknessInches
                actualThicknessInches
                actualLengthInches
                piecesPerBundle {
                  value
                  unit
                }
              }
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['sellerProducts'],
    { pageInput: { page: 0, size: 100 } },
    { enabled: options?.enabled }
  );
};

export const usePackItem = () => {
  const mutation = graphql(`
    mutation PackItem(
      $orderId: ID!
      $platformProductLengthId: ID!
      $quantity: QuantityInput!
    ) {
      packItem(
        orderId: $orderId
        platformProductLengthId: $platformProductLengthId
        quantity: $quantity
      ) {
        id
        price {
          amount
          currency
        }
        platformProductsLengths {
          id
        }
        quantity(mode: { mode: STANDARD }) {
          value
          unit
        }
      }
    }
  `);

  return useGraphqlMutation(mutation, ['packingList', 'orders']);
};

export const useRemovePackedItem = () => {
  const mutation = graphql(`
    mutation RemovePackedItem($orderId: ID!, $platformProductLengthId: ID!) {
      removePackedItem(
        orderId: $orderId
        platformProductLengthId: $platformProductLengthId
      )
    }
  `);

  return useGraphqlMutation(mutation, ['packingList', 'orders']);
};

export const usePublishPackingList = () => {
  const mutation = graphql(`
    mutation PublishPackingList($orderId: ID!) {
      publishPackingList(orderId: $orderId)
    }
  `);

  return useGraphqlMutation(mutation, ['packingList', 'orders']);
};

export const useSavePackingList = () => {
  const mutation = graphql(`
    mutation SavePackingList(
      $orderId: ID!
      $items: [PackItemInput!]!
      $notes: String
    ) {
      savePackingList(orderId: $orderId, items: $items, notes: $notes) {
        id
        piecesPerBundleOverride
        price {
          amount
          currency
        }
        platformProductsLengths {
          id
          isNominal
          lengthDisplayValue
          length {
            nominalLengthInches
            actualLengthInches
          }
          platformProduct {
            name
            standardUnitsCode
            nominalWidthInches
            actualWidthInches
            nominalThicknessInches
            actualThicknessInches
            actualLengthInches
            piecesPerBundle {
              value
              unit
            }
          }
        }
        quantity(mode: { mode: STANDARD }) {
          value
          unit
        }
        piecesQuantity: quantity(mode: { mode: STORED }) {
          value
        }
      }
    }
  `);

  return useGraphqlMutation(mutation, ['packingList', 'orders']);
};
